import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import { formatGameTimer, parseStageTimerSeconds, resolveCountdownElapsedSeconds } from '../../utils/gameTimer';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './super-squad.css';

const STAGE_NUMBER = 7;
const PARTICIPATION_POINTS = 1;
const SLOGAN_MAX = 150;
const SLOGAN_BRAIN_LOGO = '/img/brain.png';
const CARD_FIELD_MAX = 80;
const CARDS_PER_PAGE = 3;
const INITIAL_SQUAD_CARDS = 3;
const MAX_SQUAD_CARDS = 30;
const DEFAULT_PLAY_SECONDS = 300;
const STAGE4_PLAY_DEADLINE_PREFIX = 'stage4_play_deadline';

let squadAvatarSeedCounter = 0;

function createSquadCard(index) {
  const isTeam = index === 0;
  return {
    type: isTeam ? 'team' : 'member',
    name: '',
    power: '',
    ...(isTeam ? {} : { avatarSeed: `s4-hero-${++squadAvatarSeedCounter}` }),
  };
}

function isSquadCardEmpty(card, index) {
  if (index === 0) {
    return !String(card?.name ?? '').trim();
  }
  return !String(card?.name ?? '').trim() && !String(card?.power ?? '').trim();
}

const INITIAL_SQUAD_STATE = Array.from({ length: INITIAL_SQUAD_CARDS }, (_, index) =>
  createSquadCard(index)
);

const MEMBER_CARD_COLORS = [
  'linear-gradient(180deg, #8b5cf6 0%, #6345ed 100%)',
  'linear-gradient(180deg, #3b82f6 0%, #1e60c1 100%)',
  'linear-gradient(180deg, #c084fc 0%, #9333ea 100%)',
  'linear-gradient(180deg, #4ade80 0%, #2d914d 100%)',
  'linear-gradient(180deg, #60a5fa 0%, #1e3a5f 100%)',
  'linear-gradient(180deg, #f472b6 0%, #db2777 100%)',
];

const DECOR_COLORS = ['#a78bfa', '#60a5fa', '#f472b6', '#fbbf24', '#34d399'];

function parseSuperSquadDescription(raw) {
  const lines = String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  let teamName = '';
  let slogan = '';
  const members = [];
  let currentMember = null;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (/^team\s*name$/i.test(line)) {
      teamName = lines[i + 1] || '';
      i += 1;
      continue;
    }

    if (/^slogan$/i.test(line)) {
      slogan = lines[i + 1] || '';
      i += 1;
      continue;
    }

    const arrowMatch = line.match(/^(.+?)\s*(?:→|->|—|–|-)\s*(.+)$/);
    if (arrowMatch) {
      const left = arrowMatch[1].trim();
      const right = arrowMatch[2].trim();

      if (/^power$/i.test(left)) {
        if (currentMember) currentMember.power = right;
        continue;
      }

      if (currentMember) members.push(currentMember);
      currentMember = { name: left, role: right, power: '' };
      continue;
    }

    if (/^power$/i.test(line) && lines[i + 1]) {
      if (currentMember) currentMember.power = lines[i + 1];
      i += 1;
    }
  }

  if (currentMember) members.push(currentMember);

  return {
    teamName: teamName.replace(/^["']|["']$/g, '').trim(),
    slogan: slogan.replace(/^["']|["']$/g, '').trim(),
    members,
  };
}

function characterImageUrl(seed) {
  const safeSeed = encodeURIComponent(String(seed ?? 'hero'));
  return `https://api.dicebear.com/7.x/adventurer/png?seed=${safeSeed}&size=160&backgroundColor=b6e3f4,c0aede,d1d4f9`;
}

function buildSquadAnswer(slogan, cards) {
  const teamCard = cards[0] || { name: '', power: '' };
  const members = cards
    .slice(1)
    .map((card) => ({
      name: String(card.name ?? '').trim(),
      power: String(card.power ?? '').trim(),
    }))
    .filter((m) => m.name || m.power);

  return JSON.stringify({
    slogan: String(slogan ?? '').trim(),
    team: {
      name: String(teamCard.name ?? '').trim(),
      power: String(teamCard.power ?? '').trim(),
    },
    members,
  });
}

function stage4PlayDeadlineKey(userId, sessionId) {
  return `${STAGE4_PLAY_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function readPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return 0;
  try {
    const deadline = Number(sessionStorage.getItem(stage4PlayDeadlineKey(userId, sessionId)) || 0);
    return Number.isFinite(deadline) ? deadline : 0;
  } catch {
    return 0;
  }
}

function setPlayDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage4PlayDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function clearPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage4PlayDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function clearIntervalTimer(timerRef) {
  if (timerRef.current) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }
}

function ClockIcon() {
  return (
    <svg className="s4-timer-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

function DecorPiece({ className, color }) {
  return (
    <span className={`s4-decor-piece ${className}`} aria-hidden>
      <svg viewBox="0 0 48 48">
        <path
          d="M4 4h14v6c0 3 3 3 6 0V4h14v14h-6c-3 0-3 3 0 6h6v14H24v-6c0-3-3-3-6 0v6H4V24h6c3 0 3-3 0-6H4V4Z"
          fill={color}
        />
      </svg>
    </span>
  );
}

function ShieldIcon() {
  return (
    <svg className="s4-team-shield" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2 4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3Zm0 2.18 6 2.25v4.66c0 3.87-2.55 7.5-6 8.56-3.45-1.06-6-4.69-6-8.56V6.43l6-2.25ZM11 8v5.59l3.3 3.3 1.4-1.42L13 12.17V8h-2Z"
      />
    </svg>
  );
}

function SquadDisplayCard({ item, index, imageError, onImageError }) {
  const isTeam = item.type === 'team';
  const bg = MEMBER_CARD_COLORS[index % MEMBER_CARD_COLORS.length];
  const showImage = !isTeam && !imageError;

  if (isTeam) {
    return (
      <article className="s4-card s4-card--display s4-card--team" style={{ background: bg }}>
        <p className="s4-card-label">TEAM NAME</p>
        <p className="s4-team-name">{item.name}</p>
        <ShieldIcon />
      </article>
    );
  }

  return (
    <article className="s4-card s4-card--display s4-card--member" style={{ background: bg }}>
      <div className="s4-member-avatar-wrap">
        {showImage ? (
          <img
            src={characterImageUrl(`display-${index}-${item.name || 'hero'}`)}
            alt=""
            className="s4-member-avatar"
            onError={() => onImageError(index)}
          />
        ) : (
          <span className="s4-member-avatar-fallback" aria-hidden>
            <i className="fa-solid fa-user-astronaut" />
          </span>
        )}
      </div>
      <p className="s4-member-name">{item.name}</p>
      {item.role ? <p className="s4-member-role">{item.role}</p> : null}
      {item.power ? (
        <>
          <p className="s4-power-label">
            <i className="fa-solid fa-bolt" aria-hidden />
            Power
          </p>
          <p className="s4-power-text">{item.power}</p>
        </>
      ) : null}
    </article>
  );
}

function SquadCarousel({
  pages,
  page,
  maxPage,
  onNext,
  onPrev,
  disabled,
  renderCard,
  dotsClassName = '',
  canAddMore = false,
}) {
  const showPlus = page < maxPage || canAddMore;

  return (
    <>
      <div className="s4-carousel-shell">
        <div className="s4-carousel-viewport">
          <div
            className="s4-carousel-track"
            style={{ transform: `translateX(-${page * 100}%)` }}
          >
            {pages.map((pageItems, pageIndex) => (
              <div key={`page-${pageIndex}`} className="s4-carousel-page">
                {pageItems.map((item, offset) =>
                  renderCard(item, pageIndex * CARDS_PER_PAGE + offset, pageIndex, offset)
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="s4-carousel-nav">
          {page > 0 ? (
            <button
              type="button"
              className="s4-carousel-btn"
              onClick={onPrev}
              aria-label="Previous cards"
              disabled={disabled}
            >
              <i className="fa-solid fa-minus" aria-hidden />
            </button>
          ) : null}
          {showPlus ? (
            <button
              type="button"
              className="s4-carousel-btn s4-carousel-btn--add"
              onClick={onNext}
              aria-label="More cards"
              disabled={disabled}
            >
              <i className="fa-solid fa-plus" aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      {pages.length > 1 ? (
        <div className={`s4-carousel-dots ${dotsClassName}`.trim()} aria-hidden>
          {pages.map((_, i) => (
            <span
              key={`dot-${i}`}
              className={`s4-carousel-dot${page === i ? ' s4-carousel-dot--active' : ''}`}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function SquadInputCard({
  card,
  index,
  disabled,
  imageError,
  onImageError,
  onChange,
  shake,
}) {
  const isTeam = card.type === 'team';
  const label = isTeam ? 'TEAM NAME' : `HERO ${index}`;
  const bg = MEMBER_CARD_COLORS[index % MEMBER_CARD_COLORS.length];
  const showImage = !isTeam && !imageError;
  const avatarSeed = card.avatarSeed ?? `member-${index}`;

  return (
    <article
      className={`s4-card s4-card--input${isTeam ? ' s4-card--team' : ' s4-card--member'}`}
      style={{ background: bg }}
    >
      <p className="s4-card-label">{label}</p>

      {!isTeam ? (
        <div className="s4-member-avatar-wrap">
          {showImage ? (
            <img
              src={characterImageUrl(avatarSeed)}
              alt=""
              className="s4-member-avatar"
              onError={() => onImageError(index)}
            />
          ) : (
            <span className="s4-member-avatar-fallback" aria-hidden>
              <i className="fa-solid fa-user-astronaut" />
            </span>
          )}
        </div>
      ) : (
        <span className="s4-team-icon" aria-hidden>
          <i className="fa-solid fa-shield-halved" />
        </span>
      )}

      <div className="s4-card-fields">
        <label className="s4-field-label" htmlFor={`s4-card-name-${index}`}>
          Name
        </label>
        <input
          id={`s4-card-name-${index}`}
          type="text"
          className={`s4-card-input${shake && isTeam ? ' s4-card-input--shake' : ''}`}
          value={card.name}
          onChange={(ev) => onChange(index, 'name', ev.target.value)}
          maxLength={CARD_FIELD_MAX}
          placeholder={isTeam ? 'Team name' : 'Hero name'}
          disabled={disabled}
        />

        {!isTeam ? (
          <>
            <label className="s4-field-label" htmlFor={`s4-card-power-${index}`}>
              <i className="fa-solid fa-bolt" aria-hidden />
              Power
            </label>
            <input
              id={`s4-card-power-${index}`}
              type="text"
              className="s4-card-input"
              value={card.power}
              onChange={(ev) => onChange(index, 'power', ev.target.value)}
              maxLength={CARD_FIELD_MAX}
              placeholder="Superpower"
              disabled={disabled}
            />
          </>
        ) : null}
      </div>
    </article>
  );
}

export default function Stage4({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(4);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage4 = themeData?.stages_data?.stage_7;

  const playSeconds = useMemo(
    () => parseStageTimerSeconds(stage4?.timer, DEFAULT_PLAY_SECONDS),
    [stage4?.timer]
  );

  const visuals = useMemo(
    () => stageThemeVisuals(themeData, { accentFallback: '#6345ed' }),
    [themeData]
  );

  const stageLogoUrl = s3PublicUrl(stage4?.logo ?? stage4?.badge);
  const rulesList = getStageRulesList(stage4);
  const subtitle = String(stage4?.title ?? '').trim();

  const squadData = useMemo(
    () => parseSuperSquadDescription(stage4?.description),
    [stage4?.description]
  );

  const displayTeamName = squadData.teamName || 'The Productivity Protectors';
  const displaySlogan = squadData.slogan || 'United We Plan, Together We Win!';
  const displayMembers = squadData.members.length > 0 ? squadData.members : [];

  const displayItems = useMemo(() => {
    const items = [{ type: 'team', name: displayTeamName }];
    displayMembers.forEach((member) => {
      items.push({
        type: 'member',
        name: member.name,
        role: member.role,
        power: member.power,
      });
    });
    return items;
  }, [displayTeamName, displayMembers]);

  const displayCarouselPages = useMemo(() => {
    const pages = [];
    for (let i = 0; i < displayItems.length; i += CARDS_PER_PAGE) {
      pages.push(displayItems.slice(i, i + CARDS_PER_PAGE));
    }
    return pages.length > 0 ? pages : [[{ type: 'team', name: displayTeamName }]];
  }, [displayItems, displayTeamName]);

  const maxDisplayCarouselPage = displayCarouselPages.length - 1;

  const isConfigured = !!stage4 && String(stage4?.title ?? '').trim().length > 0;

  const completeSentRef = useRef(false);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const playTimerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);

  const [slogan, setSlogan] = useState('');
  const [squadCards, setSquadCards] = useState(INITIAL_SQUAD_STATE);
  const [displayCarouselPage, setDisplayCarouselPage] = useState(0);
  const [carouselPage, setCarouselPage] = useState(0);
  const [message, setMessage] = useState('');
  const [sloganShake, setSloganShake] = useState(false);
  const [teamShake, setTeamShake] = useState(false);
  const [sloganBrainFailed, setSloganBrainFailed] = useState(false);
  const [stageLogoFailed, setStageLogoFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [displayImageErrors, setDisplayImageErrors] = useState({});
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted,
    user,
    adminToken,
    navigate,
  });

  const stageRulesModal = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage4,
  });
  const { showRules, openRules, closeRules } = stageRulesModal;
  const rulesDismissed = stageRulesModal.rulesDismissed;

  const countdownDisplay = timerReady ? formatGameTimer(secondsLeft) : formatGameTimer(playSeconds);
  const inputsLocked = submitted || timerExpired;

  const maxCarouselPage = Math.ceil(squadCards.length / CARDS_PER_PAGE) - 1;
  const canAddMoreCards = squadCards.length < MAX_SQUAD_CARDS;

  const pageStyle = useMemo(() => buildStagePageStyle(visuals, 's4'), [visuals]);

  useEffect(() => {
    setStageLogoFailed(false);
  }, [stageLogoUrl]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    clearIntervalTimer(playTimerRef);

    const finishPlayTime = () => {
      clearIntervalTimer(playTimerRef);
      setSecondsLeft(0);
      setTimerExpired(true);
      clearPlayDeadline(user);
      if (!autoSubmitRef.current) {
        autoSubmitRef.current = true;
        handleSubmitRef.current?.(null, { auto: true });
      }
    };

    const startPlayCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setTimerReady(true);
      setSecondsLeft(safeRemaining);
      setTimerExpired(safeRemaining <= 0);

      if (safeRemaining <= 0) {
        finishPlayTime();
        return;
      }

      let remaining = safeRemaining;
      playTimerRef.current = window.setInterval(() => {
        remaining -= 1;
        const next = Math.max(0, remaining);
        setSecondsLeft(next);
        if (next <= 0) {
          finishPlayTime();
        }
      }, 1000);
    };

    const now = Date.now();
    let playDeadline = readPlayDeadline(user);
    if (!playDeadline) {
      playDeadline = now + playSeconds * 1000;
      setPlayDeadline(user, playDeadline);
    }

    if (now >= playDeadline) {
      setTimerReady(true);
      finishPlayTime();
      return undefined;
    }

    startPlayCountdown((playDeadline - now) / 1000);

    return () => clearIntervalTimer(playTimerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    playCheckReady,
    playSeconds,
    rulesDismissed,
    submitted,
    user,
    user?.userId,
    user?.sessionId,
  ]);

  const handleCardChange = (index, field, value) => {
    if (inputsLocked) return;
    setSquadCards((prev) =>
      prev.map((card, i) =>
        i === index ? { ...card, [field]: String(value).slice(0, CARD_FIELD_MAX) } : card
      )
    );
    if (message) setMessage('');
  };

  const handleImageError = (index) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  const handleDisplayImageError = (index) => {
    setDisplayImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  const handleDisplayCarouselNext = () => {
    setDisplayCarouselPage((p) => Math.min(maxDisplayCarouselPage, p + 1));
  };

  const handleDisplayCarouselPrev = () => {
    setDisplayCarouselPage((p) => Math.max(0, p - 1));
  };

  const handleCarouselNext = () => {
    if (carouselPage < maxCarouselPage) {
      setCarouselPage((page) => page + 1);
      return;
    }
    if (squadCards.length >= MAX_SQUAD_CARDS) return;

    setSquadCards((prev) => {
      const next = [...prev];
      while (next.length < prev.length + CARDS_PER_PAGE && next.length < MAX_SQUAD_CARDS) {
        next.push(createSquadCard(next.length));
      }
      return next;
    });
    setCarouselPage((page) => page + 1);
  };

  const handleCarouselPrev = () => {
    const isLastPage = carouselPage === maxCarouselPage;
    const pageStart = carouselPage * CARDS_PER_PAGE;
    const cardsOnPage = squadCards.slice(pageStart);
    const shouldRemovePage =
      isLastPage &&
      squadCards.length > INITIAL_SQUAD_CARDS &&
      cardsOnPage.every((card, offset) => isSquadCardEmpty(card, pageStart + offset));

    if (shouldRemovePage) {
      setSquadCards((prev) => prev.slice(0, pageStart));
      setCarouselPage((page) => Math.max(0, page - 1));
      setImageErrors((prev) => {
        const next = { ...prev };
        for (let i = pageStart; i < squadCards.length; i += 1) {
          delete next[i];
        }
        return next;
      });
      if (message) setMessage('');
      return;
    }

    setCarouselPage((page) => Math.max(0, page - 1));
  };

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitted) return;

    if (!auto && String(slogan ?? '').trim() === '') {
      setSloganShake(true);
      window.setTimeout(() => setSloganShake(false), 500);
      setMessage('Please enter your team slogan');
      return;
    }

    if (!auto && String(squadCards[0]?.name ?? '').trim() === '') {
      setTeamShake(true);
      window.setTimeout(() => setTeamShake(false), 500);
      setMessage('Please enter your team name');
      return;
    }

    const playDeadline = readPlayDeadline(user);
    const stageTimeSeconds = resolveCountdownElapsedSeconds({
      totalSeconds: playSeconds,
      secondsLeft,
      deadlineMs: playDeadline,
    });

    clearIntervalTimer(playTimerRef);
    clearPlayDeadline(user);

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    const finalPoints = PARTICIPATION_POINTS;
    const trimmedAnswer = buildSquadAnswer(slogan, squadCards);

    if (isDemoSubscribeRedirectUser(user)) {
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setSubmitted(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: trimmedAnswer,
          allCorrect: true,
        });
      }
      return;
    }

    setMessage('');
    beginSubmit();
    setSubmitted(true);

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      if (!Number.isFinite(nextDay) || nextDay < 1) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: nextDay + 1,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, stageTimeSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: trimmedAnswer,
        allCorrect: true,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
      });
    } catch (err) {
      setSubmitted(false);
      autoSubmitRef.current = false;
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect: true,
      });
    }

    nextPathRef.current = path;
    setRevealCompletedDays(completedDaysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const revealConfettiPieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 19) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  const carouselPages = useMemo(() => {
    const pages = [];
    for (let i = 0; i < squadCards.length; i += CARDS_PER_PAGE) {
      pages.push(squadCards.slice(i, i + CARDS_PER_PAGE));
    }
    return pages;
  }, [squadCards]);

  return (
    <div className="stage4-page" style={pageStyle}>
      {DECOR_COLORS.map((color, i) => (
        <DecorPiece key={color} className={`s4-decor-piece--p${i + 1}`} color={color} />
      ))}

      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 7 is not configured: set stage_7 title and description in the theme.
          </p>
        }
      >
        <>
          <header className="s4-top-bar">
            <div
              className={`s4-top-timer${timerExpired ? ' s4-top-timer--expired' : ''}`}
              aria-label="Stage timer"
              aria-live="polite"
              role="timer"
            >
              <ClockIcon />
              <span className="s4-timer-value">{countdownDisplay}</span>
            </div>
            {rulesList.length > 0 ? (
              <button
                type="button"
                className="stage4-rules-fab"
                onClick={openRules}
                aria-label="How to play"
                title="How to play"
              >
                ?
              </button>
            ) : null}
          </header>

          <div className="s4-hero">
            {stageLogoUrl && !stageLogoFailed ? (
              <img
                src={stageLogoUrl}
                alt=""
                className="s4-stage-logo"
                onError={() => setStageLogoFailed(true)}
              />
            ) : null}
            {subtitle ? <p className="s4-hero-subtitle">{subtitle}</p> : null}
            <span className="s4-hero-line" aria-hidden />
          </div>

          <div className="s4-game-row">
          <div className="s4-main-layout">
            <div className="s4-main-columns">
            <div className="s4-main-left">
              <div className="s4-section s4-section--example stage4-card">
                <p className="s4-section-label">Example</p>
                <div className="s4-slogan-bar s4-slogan-bar--display">
                  <div className="s4-slogan-label-wrap">
                    {!sloganBrainFailed ? (
                      <img
                        src={SLOGAN_BRAIN_LOGO}
                        alt=""
                        className="s4-slogan-logo"
                        onError={() => setSloganBrainFailed(true)}
                        aria-hidden
                      />
                    ) : (
                      <span className="s4-slogan-logo-fallback" aria-hidden>
                        <i className="fa-solid fa-puzzle-piece" />
                      </span>
                    )}
                    <span className="s4-slogan-label">TEAM SLOGAN</span>
                  </div>
                  <span className="s4-slogan-divider" aria-hidden />
                  <p className="s4-slogan-text">&ldquo;{displaySlogan}&rdquo;</p>
                </div>

                <SquadCarousel
                  pages={displayCarouselPages}
                  page={displayCarouselPage}
                  maxPage={maxDisplayCarouselPage}
                  onNext={handleDisplayCarouselNext}
                  onPrev={handleDisplayCarouselPrev}
                  disabled={false}
                  dotsClassName="s4-carousel-dots--display"
                  renderCard={(item, index) => (
                    <SquadDisplayCard
                      key={`display-${index}`}
                      item={item}
                      index={index}
                      imageError={!!displayImageErrors[index]}
                      onImageError={handleDisplayImageError}
                    />
                  )}
                />
              </div>
            </div>

            <div className="s4-main-right stage4-card s4-section s4-section--yours">
              <p className="s4-section-label">Your squad</p>
              <div className="s4-slogan-bar">
                <div className="s4-slogan-label-wrap">
                  {!sloganBrainFailed ? (
                    <img
                      src={SLOGAN_BRAIN_LOGO}
                      alt=""
                      className="s4-slogan-logo"
                      onError={() => setSloganBrainFailed(true)}
                      aria-hidden
                    />
                  ) : (
                    <span className="s4-slogan-logo-fallback" aria-hidden>
                      <i className="fa-solid fa-puzzle-piece" />
                    </span>
                  )}
                  <label className="s4-slogan-label" htmlFor="stage4-slogan">
                    TEAM SLOGAN
                  </label>
                </div>
                <span className="s4-slogan-divider" aria-hidden />
                <input
                  id="stage4-slogan"
                  type="text"
                  className={`s4-slogan-input${sloganShake ? ' s4-slogan-input--shake' : ''}`}
                  value={slogan}
                  onChange={(ev) => {
                    setSlogan(ev.target.value.slice(0, SLOGAN_MAX));
                    if (message) setMessage('');
                  }}
                  maxLength={SLOGAN_MAX}
                  placeholder="Your Team Slogan"
                  disabled={inputsLocked}
                />
              </div>

              <SquadCarousel
                pages={carouselPages}
                page={carouselPage}
                maxPage={maxCarouselPage}
                canAddMore={canAddMoreCards}
                onNext={handleCarouselNext}
                onPrev={handleCarouselPrev}
                disabled={inputsLocked}
                renderCard={(card, index) => (
                  <SquadInputCard
                    key={`input-${index}`}
                    card={card}
                    index={index}
                    disabled={inputsLocked}
                    imageError={!!imageErrors[index]}
                    onImageError={handleImageError}
                    onChange={handleCardChange}
                    shake={teamShake}
                  />
                )}
              />
            </div>
            </div>
          </div>
          <div className="stage-page-puzzle-aside stage3-puzzle-aside">
            <StagePuzzleReveal themeData={themeData} />
          </div>
          </div>

          <div className="s4-main-submit">
            {!submitted ? (
              <form className="stage4-form stage4-form--submit" onSubmit={handleSubmit}>
                <button
                  type="submit"
                  className="stage4-submit"
                  disabled={
                    submitted ||
                    String(slogan ?? '').trim() === '' ||
                    String(squadCards[0]?.name ?? '').trim() === ''
                  }
                >
                  Submit
                </button>
              </form>
            ) : (
              <p className="stage4-complete">Thanks! Your super squad has been submitted.</p>
            )}

            {message ? <div className="stage4-hint">{message}</div> : null}
          </div>

          {showRevealOverlay ? (
            <div
              className="stage-reveal-overlay"
              role="dialog"
              aria-modal="true"
              aria-label="Revealing your puzzle progress"
              onClick={handleRevealOverlayClick}
            >
              <div className="stage-reveal-confetti" aria-hidden>
                {revealConfettiPieces.map((piece) => (
                  <span
                    key={piece.id}
                    className="stage-reveal-confetti-piece"
                    style={{
                      left: piece.left,
                      animationDelay: piece.delay,
                      animationDuration: piece.duration,
                    }}
                  />
                ))}
              </div>
              <div className="stage-reveal-stage">
                <aside className="stage-reveal-panel" aria-label="Puzzle pieces progress">
                  <StagePuzzleReveal
                    themeData={themeData}
                    completedDaysOverride={revealCompletedDays}
                    animateReveal
                  />
                </aside>
              </div>
            </div>
          ) : null}
        </>
      </StagePlayGate>

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage4-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage4-rules-title"
          onClick={closeRules}
        >
          <div className="stage4-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage4-rules-title">How to play</h2>
            <ul className="stage4-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage4-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
